import { Building2, User, Briefcase, MapPin, IndianRupee, Clock, Mail, Phone, Link as LinkIcon, Users, FileText, Calendar, MailOpen, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

/**
 * Detailed view of an Enquiry (contact | hire_talent | callback).
 * Shows every populated field grouped into logical sections.
 */
export default function EnquiryDetailDialog({ enquiry, open, onClose, onToggleRead, onDelete }) {
  if (!enquiry) return null;
  const e = enquiry;
  const isHire = e.type === "hire_talent";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="flex max-h-[90vh] w-[95vw] max-w-2xl flex-col gap-0 overflow-hidden p-0"
        data-testid="enquiry-detail-dialog"
      >
        <DialogHeader className="shrink-0 border-b border-slate-100 p-5">
          <div className="flex flex-wrap items-center gap-2">
            <DialogTitle className="text-lg">{e.name || "Anonymous"}</DialogTitle>
            <Badge className={
              isHire ? "bg-secondary/15 text-secondary" :
              e.type === "callback" ? "bg-blue-100 text-blue-700" :
              "bg-slate-100 text-slate-700"
            }>{e.type}</Badge>
            {!e.is_read && <Badge className="bg-blue-100 text-blue-700">Unread</Badge>}
          </div>
          <p className="mt-1 text-left text-xs text-slate-500">
            <Calendar className="mr-1 inline h-3 w-3" />
            Received {new Date(e.created_at).toLocaleString()}
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-5 text-sm">
          <div className="space-y-5">
          {/* Contact block */}
          <Section title="Contact">
            <Row icon={User} label="Name" value={e.name} />
            {e.designation && <Row icon={Briefcase} label="Designation" value={e.designation} />}
            <Row icon={Mail} label="Email" value={e.email ? <a href={`mailto:${e.email}`} className="text-primary hover:underline">{e.email}</a> : "—"} />
            <Row icon={Phone} label="Phone" value={e.phone ? <a href={`tel:${e.phone}`} className="text-primary hover:underline">{e.phone}</a> : "—"} />
            {e.preferred_time && <Row icon={Clock} label="Preferred time" value={e.preferred_time} />}
          </Section>

          {/* Company block — mostly for hire_talent */}
          {(e.company || e.company_type || e.company_size || e.website || e.address) && (
            <Section title="Company">
              {e.company && <Row icon={Building2} label="Company" value={e.company} />}
              {e.company_type && <Row icon={Briefcase} label="Type" value={e.company_type} />}
              {e.company_size && <Row icon={Users} label="Size" value={e.company_size} />}
              {e.website && <Row icon={LinkIcon} label="Website" value={<a href={e.website.startsWith("http") ? e.website : `https://${e.website}`} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">{e.website}</a>} />}
              {e.address && <Row icon={MapPin} label="Address" value={e.address} />}
            </Section>
          )}

          {/* Requirement — only meaningful for hire_talent */}
          {(e.role_needed || e.department || e.headcount || e.job_location || e.experience_required || e.salary_range || e.urgency) && (
            <Section title="Requirement">
              {e.role_needed && <Row icon={Briefcase} label="Role" value={<b>{e.role_needed}</b>} />}
              {e.department && <Row icon={FileText} label="Department" value={e.department} />}
              {e.headcount && <Row icon={Users} label="Headcount" value={e.headcount} />}
              {e.job_location && <Row icon={MapPin} label="Location" value={e.job_location} />}
              {e.experience_required && <Row icon={Clock} label="Experience" value={e.experience_required} />}
              {e.salary_range && <Row icon={IndianRupee} label="Salary / CTC" value={e.salary_range} />}
              {e.urgency && <Row icon={Clock} label="Urgency" value={e.urgency} />}
            </Section>
          )}

          {/* Message body */}
          {(e.subject || e.message) && (
            <Section title="Message">
              {e.subject && <div className="mb-2 text-sm font-semibold text-slate-800">{e.subject}</div>}
              <pre className="max-h-[300px] overflow-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-3 font-sans text-sm leading-relaxed text-slate-700" data-testid="enquiry-detail-message">{e.message || "(no message)"}</pre>
            </Section>
          )}
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-slate-100 bg-slate-50/50 p-4">
          <div className="flex flex-wrap gap-2">
            {e.email && <a href={`mailto:${e.email}`}><Button variant="outline" size="sm">Reply via email</Button></a>}
            {e.phone && <a href={`tel:${e.phone}`}><Button variant="outline" size="sm">Call</Button></a>}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" size="sm" onClick={() => onToggleRead && onToggleRead(e)} data-testid="enquiry-detail-toggle-read">
              <MailOpen className="mr-1 h-3.5 w-3.5" /> {e.is_read ? "Mark unread" : "Mark read"}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onDelete && onDelete(e)} data-testid="enquiry-detail-delete">
              <Trash2 className="mr-1 h-3.5 w-3.5 text-rose-600" /> Delete
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-slate-400">{title}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2 text-slate-700">
      {Icon && <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />}
      <span className="min-w-[110px] text-slate-500">{label}:</span>
      <span className="text-slate-900">{value}</span>
    </div>
  );
}
